import { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { downloadBlob } from '../services/api';
import MonthPicker from '../components/MonthPicker';
import { useCutoffDay, firstIssueMonth, lastIssueMonth, monthLabel, monthStart, ordinal, showDate, todayIso } from '../utils/months';

const styles = {
  back: { color: '#4a9eff', textDecoration: 'none', fontSize: 13, display: 'inline-block', marginBottom: 12 },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  h1: { fontSize: 22, fontWeight: 600 },
  section: { backgroundColor: '#fff', borderRadius: 8, padding: 20, marginBottom: 20, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  sectionTitle: { fontSize: 16, fontWeight: 600, marginBottom: 12 },
  grid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' },
  fieldLabel: { fontSize: 12, color: '#888', textTransform: 'uppercase' },
  fieldValue: { fontSize: 14, marginBottom: 8 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '8px 12px', fontSize: 12, fontWeight: 600, color: '#666', textTransform: 'uppercase', borderBottom: '2px solid #eee' },
  td: { padding: '8px 12px', borderBottom: '1px solid #f0f0f0', fontSize: 14 },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10, fontSize: 12, fontWeight: 500,
    backgroundColor: color === 'green' ? '#e6f9e6' : color === 'amber' ? '#fff8e6' : color === 'red' ? '#fee' : '#f0f0f0',
    color: color === 'green' ? '#27ae60' : color === 'amber' ? '#f39c12' : color === 'red' ? '#e74c3c' : '#666',
  }),
  btn: { padding: '8px 16px', backgroundColor: '#4a9eff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSmall: { padding: '4px 10px', backgroundColor: '#4a9eff', color: '#fff', border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' },
  btnDanger: { padding: '8px 16px', backgroundColor: '#fff', color: '#e74c3c', border: '1px solid #e74c3c', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  btnSecondary: { padding: '8px 16px', backgroundColor: '#fff', color: '#333', border: '1px solid #ddd', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  btnRow: { display: 'flex', gap: 8 },
  modal: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.4)', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  modalCard: { backgroundColor: '#fff', borderRadius: 8, padding: 24, width: 440, boxShadow: '0 8px 32px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: 18, fontWeight: 600, marginBottom: 16 },
  label: { display: 'block', fontSize: 13, fontWeight: 500, color: '#555', marginBottom: 4 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  select: { width: '100%', padding: '8px 10px', border: '1px solid #ddd', borderRadius: 4, fontSize: 14, marginBottom: 12 },
  row: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  btnRowModal: { display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 },
  btnCancel: { padding: '8px 16px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: 4, fontSize: 13, cursor: 'pointer' },
  link: { color: '#4a9eff', textDecoration: 'none' },
};

const statusColor = (s) => s === 'active' ? 'green' : s === 'pending_renewal' ? 'amber' : s === 'expired' ? 'red' : 'gray';

export default function SubscriberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [titles, setTitles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [showSubModal, setShowSubModal] = useState(false);
  // the subscription whose dates are being edited ({ id, title_name, start_date, end_date })
  const [editSub, setEditSub] = useState(null);
  const [editSubError, setEditSubError] = useState('');
  const [subForm, setSubForm] = useState({ title_id: '', start_date: todayIso(), duration_months: 12, amount_paid: '', payment_method: 'card' });
  // sold by the month (Settings): dates are months, a new one starts with the next issue the cut-off allows
  const cutoff = useCutoffDay();

  const load = useCallback(() => {
    Promise.all([
      api.getSubscriber(id),
      api.getTitles(),
    ]).then(([sub, t]) => {
      setData(sub);
      setTitles(t);
      setForm(sub);
    }).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    await api.updateSubscriber(id, form);
    setEditing(false);
    load();
  };

  const handleDelete = async () => {
    if (!window.confirm(`Permanently delete ${data.first_name} ${data.last_name}? This will also delete all their subscriptions.`)) return;
    await api.deleteSubscriber(id);
    navigate('/subscribers');
  };

  const handleAddSubscription = async () => {
    if (!subForm.title_id || !subForm.start_date) return;
    await api.createSubscription({ subscriber_id: parseInt(id), ...subForm, amount_paid: subForm.amount_paid || null });
    setShowSubModal(false);
    load();
  };

  const openAddSub = () => {
    setSubForm({ title_id: '', start_date: cutoff ? firstIssueMonth(cutoff) : todayIso(), duration_months: 12, amount_paid: '', payment_method: 'card' });
    setShowSubModal(true);
  };

  const handlePrintLabel = async () => {
    try {
      const result = await api.generateSubscriberLabels([id]);
      downloadBlob(result.blob, `label-${data.last_name}-${data.first_name}.pdf`);
    } catch (err) {
      alert(err.message || 'Failed to generate label');
    }
  };

  // Dates as stored (the API gives UTC midnight: the first 10 characters are the day)
  const openEditSub = (s) => {
    setEditSubError('');
    const start = String(s.start_date).slice(0, 10);
    const end = String(s.end_date).slice(0, 10);
    // by the month the pickers hold months (the server stores the whole month)
    setEditSub({ id: s.id, title_name: s.title_name, start_date: cutoff ? monthStart(start) : start, end_date: cutoff ? monthStart(end) : end });
  };

  const handleSaveSubDates = async () => {
    if (!editSub.start_date || !editSub.end_date) { setEditSubError('Both dates are needed'); return; }
    if (editSub.end_date < editSub.start_date) { setEditSubError(cutoff ? 'The last issue is before the first issue' : 'The end date is before the start date'); return; }
    try {
      await api.updateSubscription(editSub.id, { start_date: editSub.start_date, end_date: editSub.end_date });
      setEditSub(null);
      load();
    } catch (err) {
      setEditSubError(err.message || 'Could not save the dates');
    }
  };

  const handleRenew = async (subId) => {
    if (!window.confirm('Renew this subscription for 12 months?')) return;
    await api.renewSubscription(subId, { duration_months: 12 });
    load();
  };

  if (loading) return <div>Loading...</div>;
  if (!data) return <div>Subscriber not found</div>;

  const setField = (field, value) => setForm(f => ({ ...f, [field]: value }));

  return (
    <div>
      <Link to="/subscribers" style={styles.back}>&larr; Back to Subscribers</Link>

      <div style={styles.header}>
        <h1 style={styles.h1}>{data.first_name} {data.last_name}</h1>
        <div style={styles.btnRow}>
          {!editing && <button style={styles.btnSecondary} onClick={() => setEditing(true)}>Edit</button>}
          {!editing && <button style={styles.btnSecondary} onClick={handlePrintLabel}>Print Label</button>}
          <button style={styles.btnDanger} onClick={handleDelete}>Delete</button>
        </div>
      </div>

      <div style={styles.section}>
        <div style={styles.sectionTitle}>Contact Details</div>
        {editing ? (
          <div>
            <label style={styles.label}>Subscriber Number</label>
            <input style={{ ...styles.input, maxWidth: 200 }} value={form.subscriber_number || ''} onChange={e => setField('subscriber_number', e.target.value)} placeholder="Optional" />
            <div style={styles.row}>
              <div>
                <label style={styles.label}>First Name</label>
                <input style={styles.input} value={form.first_name || ''} onChange={e => setField('first_name', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Last Name</label>
                <input style={styles.input} value={form.last_name || ''} onChange={e => setField('last_name', e.target.value)} />
              </div>
            </div>
            <label style={styles.label}>Company</label>
            <input style={styles.input} value={form.company || ''} onChange={e => setField('company', e.target.value)} />
            <label style={styles.label}>Address Line 1</label>
            <input style={styles.input} value={form.address_line1 || ''} onChange={e => setField('address_line1', e.target.value)} />
            <label style={styles.label}>Address Line 2</label>
            <input style={styles.input} value={form.address_line2 || ''} onChange={e => setField('address_line2', e.target.value)} />
            <div style={styles.row}>
              <div>
                <label style={styles.label}>City</label>
                <input style={styles.input} value={form.city || ''} onChange={e => setField('city', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>County</label>
                <input style={styles.input} value={form.county || ''} onChange={e => setField('county', e.target.value)} />
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Postcode</label>
                <input style={styles.input} value={form.postcode || ''} onChange={e => setField('postcode', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Country</label>
                <input style={styles.input} value={form.country || ''} onChange={e => setField('country', e.target.value)} />
              </div>
            </div>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Email</label>
                <input style={styles.input} value={form.email || ''} onChange={e => setField('email', e.target.value)} />
              </div>
              <div>
                <label style={styles.label}>Phone</label>
                <input style={styles.input} value={form.phone || ''} onChange={e => setField('phone', e.target.value)} />
              </div>
            </div>
            <label style={styles.label}>Preferred Renewal Method</label>
            <select style={styles.select} value={form.preferred_renewal_method || 'email'} onChange={e => setField('preferred_renewal_method', e.target.value)}>
              <option value="email">Email</option>
              <option value="letter">Letter</option>
              <option value="both">Both</option>
            </select>
            <div style={{ marginTop: 4, marginBottom: 12 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!(form.delivery_address_line1 || form._showDelivery)} onChange={e => {
                  if (!e.target.checked) {
                    setForm(f => ({ ...f, delivery_name: '', delivery_address_line1: '', delivery_address_line2: '', delivery_city: '', delivery_county: '', delivery_postcode: '', delivery_country: '', _showDelivery: false }));
                  } else {
                    setForm(f => ({ ...f, _showDelivery: true }));
                  }
                }} />
                Different delivery name / address
              </label>
            </div>
            {(form.delivery_address_line1 || form._showDelivery) && (
              <div style={{ padding: '12px 14px', backgroundColor: '#f8f9fa', borderRadius: 6, border: '1px solid #eee', marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#555', marginBottom: 8 }}>Delivery Name &amp; Address</div>
                <label style={styles.label}>Recipient Name</label>
                <input style={styles.input} placeholder="Leave blank to use subscriber name" value={form.delivery_name || ''} onChange={e => setField('delivery_name', e.target.value)} />
                <label style={styles.label}>Address Line 1</label>
                <input style={styles.input} value={form.delivery_address_line1 || ''} onChange={e => setField('delivery_address_line1', e.target.value)} />
                <label style={styles.label}>Address Line 2</label>
                <input style={styles.input} value={form.delivery_address_line2 || ''} onChange={e => setField('delivery_address_line2', e.target.value)} />
                <div style={styles.row}>
                  <div>
                    <label style={styles.label}>City</label>
                    <input style={styles.input} value={form.delivery_city || ''} onChange={e => setField('delivery_city', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>County</label>
                    <input style={styles.input} value={form.delivery_county || ''} onChange={e => setField('delivery_county', e.target.value)} />
                  </div>
                </div>
                <div style={styles.row}>
                  <div>
                    <label style={styles.label}>Postcode</label>
                    <input style={styles.input} value={form.delivery_postcode || ''} onChange={e => setField('delivery_postcode', e.target.value)} />
                  </div>
                  <div>
                    <label style={styles.label}>Country</label>
                    <input style={styles.input} value={form.delivery_country || ''} onChange={e => setField('delivery_country', e.target.value)} />
                  </div>
                </div>
              </div>
            )}
            <label style={styles.label}>Notes</label>
            <textarea style={{ ...styles.input, height: 60, resize: 'vertical' }} value={form.notes || ''} onChange={e => setField('notes', e.target.value)} />
            <div style={styles.btnRow}>
              <button style={styles.btnCancel} onClick={() => { setEditing(false); setForm(data); }}>Cancel</button>
              <button style={styles.btn} onClick={handleSave}>Save</button>
            </div>
          </div>
        ) : (
          <div style={styles.grid}>
            <div><div style={styles.fieldLabel}>Subscriber #</div><div style={styles.fieldValue}>{data.subscriber_number || data.id}</div></div>
            <div><div style={styles.fieldLabel}>Name</div><div style={styles.fieldValue}>{data.first_name} {data.last_name}</div></div>
            <div><div style={styles.fieldLabel}>Company</div><div style={styles.fieldValue}>{data.company || '-'}</div></div>
            <div><div style={styles.fieldLabel}>Address</div><div style={styles.fieldValue}>{data.address_line1}{data.address_line2 ? `, ${data.address_line2}` : ''}</div></div>
            <div><div style={styles.fieldLabel}>City</div><div style={styles.fieldValue}>{data.city}{data.county ? `, ${data.county}` : ''}</div></div>
            <div><div style={styles.fieldLabel}>Postcode</div><div style={styles.fieldValue}>{data.postcode}</div></div>
            <div><div style={styles.fieldLabel}>Country</div><div style={styles.fieldValue}>{data.country}</div></div>
            <div><div style={styles.fieldLabel}>Email</div><div style={styles.fieldValue}>{data.email || '-'}</div></div>
            <div><div style={styles.fieldLabel}>Phone</div><div style={styles.fieldValue}>{data.phone || '-'}</div></div>
            <div><div style={styles.fieldLabel}>Renewal Method</div><div style={styles.fieldValue}>{data.preferred_renewal_method}</div></div>
            {data.delivery_address_line1 && (
              <div style={{ gridColumn: '1 / -1', marginTop: 8, padding: '10px 14px', backgroundColor: '#f8f9fa', borderRadius: 6, border: '1px solid #eee' }}>
                <div style={{ ...styles.fieldLabel, marginBottom: 4 }}>Delivery Name &amp; Address</div>
                <div style={styles.fieldValue}>
                  {data.delivery_name && <>{data.delivery_name}<br /></>}
                  {data.delivery_address_line1}{data.delivery_address_line2 ? `, ${data.delivery_address_line2}` : ''}<br />
                  {data.delivery_city}{data.delivery_county ? `, ${data.delivery_county}` : ''} {data.delivery_postcode}
                  {data.delivery_country ? `, ${data.delivery_country}` : ''}
                </div>
              </div>
            )}
            {data.notes && <div style={{ gridColumn: '1 / -1' }}><div style={styles.fieldLabel}>Notes</div><div style={styles.fieldValue}>{data.notes}</div></div>}
          </div>
        )}
      </div>

      <div style={styles.section}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={styles.sectionTitle}>Subscriptions</div>
          <button style={styles.btn} onClick={openAddSub}>+ Add Subscription</button>
        </div>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>{cutoff ? 'First issue' : 'Start'}</th>
              <th style={styles.th}>{cutoff ? 'Expiry' : 'End'}</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {(data.subscriptions || []).map(s => (
              <tr key={s.id}>
                <td style={styles.td}>
                  <Link to={`/titles/${s.title_id}`} style={styles.link}>{s.title_name}</Link>
                </td>
                <td style={styles.td}><span style={styles.badge(statusColor(s.status))}>{s.status}</span></td>
                <td style={styles.td}>{showDate(s.start_date, cutoff)}</td>
                <td style={styles.td}>{showDate(s.end_date, cutoff)}</td>
                <td style={{ ...styles.td, whiteSpace: 'nowrap' }}>
                  {s.status !== 'cancelled' && (
                    <button style={{ ...styles.btnSmall, backgroundColor: '#fff', color: '#333', border: '1px solid #ccc', marginRight: 6 }} onClick={() => openEditSub(s)}>Edit</button>
                  )}
                  {(s.status === 'expired' || s.status === 'pending_renewal') && (
                    <button style={styles.btnSmall} onClick={() => handleRenew(s.id)}>Renew</button>
                  )}
                </td>
              </tr>
            ))}
            {(!data.subscriptions || data.subscriptions.length === 0) && (
              <tr><td style={styles.td} colSpan={5}>No subscriptions</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {data.renewals && data.renewals.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Renewal History</div>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Title</th>
                <th style={styles.th}>Method</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {data.renewals.map(r => (
                <tr key={r.id}>
                  <td style={styles.td}>{r.title_name}</td>
                  <td style={styles.td}>{r.method}</td>
                  <td style={styles.td}><span style={styles.badge(r.status === 'sent' ? 'green' : 'red')}>{r.status}</span></td>
                  <td style={styles.td}>{new Date(r.sent_at).toLocaleDateString('en-GB')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editSub && (
        <div style={styles.modal} onClick={() => setEditSub(null)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalTitle}>{editSub.title_name} — subscription dates</div>
            {editSubError && <div style={{ color: '#c0392b', marginBottom: 10, fontSize: 13 }}>{editSubError}</div>}
            {cutoff ? (
              <>
                <label style={styles.label}>First issue</label>
                <div style={{ marginBottom: 12 }}>
                  <MonthPicker style={styles.select} value={editSub.start_date} onChange={v => setEditSub({ ...editSub, start_date: v })} testId="edit-sub-start" />
                </div>
                <label style={styles.label}>Expiry (last issue)</label>
                <div style={{ marginBottom: 12 }}>
                  <MonthPicker style={styles.select} value={editSub.end_date} onChange={v => setEditSub({ ...editSub, end_date: v })} testId="edit-sub-end" />
                </div>
              </>
            ) : (
              <div style={styles.row}>
                <div>
                  <label style={styles.label}>Start Date</label>
                  <input style={styles.input} type="date" value={editSub.start_date} onChange={e => setEditSub({ ...editSub, start_date: e.target.value })} />
                </div>
                <div>
                  <label style={styles.label}>End Date</label>
                  <input style={styles.input} type="date" value={editSub.end_date} onChange={e => setEditSub({ ...editSub, end_date: e.target.value })} autoFocus />
                </div>
              </div>
            )}
            <div style={{ fontSize: 12, color: '#888', marginBottom: 12 }}>
              Moving the {cutoff ? 'expiry' : 'end date'} on (for a renewal) makes the subscription active again, and its renewal reminders start over for the new {cutoff ? 'expiry' : 'end date'}.
            </div>
            <div style={styles.btnRowModal}>
              <button style={styles.btnCancel} onClick={() => setEditSub(null)}>Cancel</button>
              <button style={styles.btn} onClick={handleSaveSubDates}>Save</button>
            </div>
          </div>
        </div>
      )}

      {showSubModal && (
        <div style={styles.modal} onClick={() => setShowSubModal(false)}>
          <div style={styles.modalCard} onClick={e => e.stopPropagation()}>
            <div style={styles.modalTitle}>Add Subscription</div>
            <label style={styles.label}>Title</label>
            <select style={styles.select} value={subForm.title_id} onChange={e => setSubForm({ ...subForm, title_id: e.target.value })}>
              <option value="">Select a title...</option>
              {titles.filter(t => t.active).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <div style={styles.row}>
              <div>
                <label style={styles.label}>{cutoff ? 'First issue' : 'Start Date'}</label>
                {cutoff ? (
                  <div style={{ marginBottom: 12 }}>
                    <MonthPicker style={styles.select} value={subForm.start_date} onChange={v => setSubForm({ ...subForm, start_date: v })} testId="add-sub-start" />
                  </div>
                ) : (
                  <input style={styles.input} type="date" value={subForm.start_date} onChange={e => setSubForm({ ...subForm, start_date: e.target.value })} />
                )}
              </div>
              <div>
                <label style={styles.label}>Duration</label>
                <select style={styles.select} value={subForm.duration_months} onChange={e => setSubForm({ ...subForm, duration_months: parseInt(e.target.value) })}>
                  <option value={3}>3 months</option>
                  <option value={6}>6 months</option>
                  <option value={12}>12 months</option>
                </select>
              </div>
            </div>
            {cutoff && subForm.start_date && (
              <div style={{ fontSize: 12, color: '#888', margin: '-4px 0 12px' }} data-testid="add-sub-expiry">
                Expiry: <b style={{ color: '#333' }}>{monthLabel(lastIssueMonth(subForm.start_date, subForm.duration_months))}</b>.
                {' '}Signed up by the {ordinal(cutoff)}, the first issue is next month's; after the {ordinal(cutoff)}, the month after.
              </div>
            )}
            <div style={styles.row}>
              <div>
                <label style={styles.label}>Amount Paid</label>
                <input style={styles.input} type="number" step="0.01" value={subForm.amount_paid} onChange={e => setSubForm({ ...subForm, amount_paid: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <label style={styles.label}>Payment Method</label>
                <select style={styles.select} value={subForm.payment_method} onChange={e => setSubForm({ ...subForm, payment_method: e.target.value })}>
                  <option value="card">Card</option>
                  <option value="cash">Cash</option>
                  <option value="cheque">Cheque</option>
                  <option value="bank transfer">Bank Transfer</option>
                </select>
              </div>
            </div>
            <div style={styles.btnRowModal}>
              <button style={styles.btnCancel} onClick={() => setShowSubModal(false)}>Cancel</button>
              <button style={styles.btn} onClick={handleAddSubscription}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
